import React, { useState, useEffect } from 'react';
import { PlayIcon, StopIcon, ClockIcon, PauseIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTasks } from '../../hooks/useTasks';
import { Task, TimeEntry } from '../../types/task';
import SearchableSelect from '../common/SearchableSelect';
import {
  startTimer,
  stopTimer,
  pauseTimer,
  resumeTimer,
  getRunningTimer,
  formatDuration,
  getElapsedTime
} from '../../services/timeTrackingService';
import toast from 'react-hot-toast';

export default function GlobalTimeTracker() {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const { tasks } = useTasks();
  const [runningTimer, setRunningTimer] = useState<TimeEntry | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Futó időmérő betöltése induláskor és amikor a tasks változik
  useEffect(() => {
    loadRunningTimer();
    
    // Futó időmérő real-time figyelése (5 másodpercenként)
    const interval = setInterval(() => {
      loadRunningTimer();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentUser, tasks]);

  const loadRunningTimer = async () => {
    if (!currentUser) return;
    
    try {
      // Keresünk futó időmérőt az összes feladatban
      for (const task of tasks) {
        const running = await getRunningTimer(task.id, currentUser.uid, task.organizationId);
        if (running) {
          setRunningTimer(running);
          setSelectedTaskId(task.id);
          
          // Csak akkor frissítsük az időt, ha nem szünetel
          if (!running.isPaused) {
            const totalElapsed = getElapsedTime(running.startTime);
            const pausedDuration = running.pausedDuration || 0;
            setElapsedTime(totalElapsed - pausedDuration);
          }
          return;
        }
      }
    } catch (error) {
      console.error('Failed to load running timer:', error);
    }
  };

  // Futó időmérő frissítése másodpercenként
  useEffect(() => {
    if (!runningTimer) return;

    const interval = setInterval(() => {
      if (runningTimer.isPaused) {
        // Ha szünetel, ne frissítsük az időt
        return;
      }
      
      const totalElapsed = getElapsedTime(runningTimer.startTime);
      const pausedDuration = runningTimer.pausedDuration || 0;
      setElapsedTime(totalElapsed - pausedDuration);
    }, 1000);

    return () => clearInterval(interval);
  }, [runningTimer]);

  const handleStart = async () => {
    if (!currentUser || !selectedTaskId) return;
    
    const task = tasks.find(t => t.id === selectedTaskId);
    if (!task) return;

    setLoading(true);
    try {
      const timer = await startTimer(
        task.id,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown',
        task.organizationId,
        description
      );
      setRunningTimer(timer);
      setDescription('');
      setIsOpen(false);
      toast.success(t('timeTracking.timerStarted') || 'Időmérő elindítva');
    } catch (error: any) {
      toast.error(error.message || 'Hiba az időmérő indításakor');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    if (!runningTimer || !currentUser) return;

    setLoading(true);
    try {
      const pausedTimer = await pauseTimer(runningTimer.id);
      setRunningTimer(pausedTimer);
      // Azonnal frissítjük az időt
      const totalElapsed = getElapsedTime(pausedTimer.startTime);
      const pausedDuration = pausedTimer.pausedDuration || 0;
      setElapsedTime(totalElapsed - pausedDuration);
      toast.success(t('timeTracking.timerPaused') || 'Időmérő szüneteltetve');
    } catch (error: any) {
      toast.error(error.message || 'Hiba az időmérő szüneteltetésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!runningTimer || !currentUser) return;

    setLoading(true);
    try {
      const resumedTimer = await resumeTimer(runningTimer.id);
      setRunningTimer(resumedTimer);
      toast.success(t('timeTracking.timerResumed') || 'Időmérő folytatva');
    } catch (error: any) {
      toast.error(error.message || 'Hiba az időmérő folytatásakor');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!runningTimer || !currentUser) return;

    setLoading(true);
    try {
      await stopTimer(runningTimer.id);
      setRunningTimer(null);
      setElapsedTime(0);
      setSelectedTaskId('');
      toast.success(t('timeTracking.timerStopped') || 'Időmérő leállítva');
    } catch (error: any) {
      toast.error(error.message || 'Hiba az időmérő leállításakor');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTask = () => {
    return tasks.find(t => t.id === selectedTaskId);
  };

  if (!currentUser) return null;

  return (
    <div className="relative">
      {runningTimer ? (
        // Futó időmérő megjelenítése - kompakt nézet
        <button
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          className={`flex items-center space-x-2 px-2 sm:px-4 py-2 rounded-lg border transition-all ${
            runningTimer.isPaused 
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}
        >
          <div className={`w-2 h-2 rounded-full ${runningTimer.isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`}></div>
          <span className={`text-base sm:text-lg font-mono font-bold ${
            runningTimer.isPaused 
              ? 'text-yellow-700 dark:text-yellow-400'
              : 'text-green-700 dark:text-green-400'
          }`}>
            {formatDuration(elapsedTime)}
          </span>
          <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">
            {runningTimer.isPaused ? '⏸️' : '▶️'}
          </span>
        </button>
      ) : (
        // Időmérő indítás gomb
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
        >
          <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
          <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:inline">
            {t('timeTracking.startTimer') || 'Időmérő indítása'}
          </span>
        </button>
      )}

      {/* Feladat választó dropdown */}
      {isOpen && !runningTimer && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-[28rem] max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('timeTracking.selectTask') || 'Válassz feladatot'}
                </label>
                <SearchableSelect
                  options={[
                    { value: '', label: t('timeTracking.selectTaskPlaceholder') || 'Válassz...' },
                    ...tasks.map(task => {
                      const displayTitle = task.title.length > 35 ? task.title.substring(0, 35) + '...' : task.title;
                      return {
                        value: task.id,
                        label: `${task.taskNumber} - ${displayTitle}`
                      };
                    })
                  ]}
                  value={selectedTaskId}
                  onChange={setSelectedTaskId}
                  placeholder={t('timeTracking.selectTaskPlaceholder') || 'Válassz feladatot...'}
                  searchPlaceholder={t('tasks.placeholders.searchTasks') || 'Keresés feladatokban...'}
                  clearable
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('timeTracking.description') || 'Leírás'} ({t('common.optional') || 'opcionális'})
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('timeTracking.placeholder') || 'Mit csinálsz?'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.cancel') || 'Mégse'}
                </button>
                <button
                  onClick={handleStart}
                  disabled={!selectedTaskId || loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PlayIcon className="h-4 w-4" />
                  <span>{t('timeTracking.start') || 'Indítás'}</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Időmérő részletek popup */}
      {isDetailsOpen && runningTimer && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsDetailsOpen(false)}
          ></div>
          <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
              {/* Feladat info */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-2 sm:pb-3">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full mt-1 ${runningTimer.isPaused ? 'bg-yellow-500' : 'bg-green-500 animate-pulse'}`}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white break-words">
                      {getCurrentTask()?.taskNumber} - {getCurrentTask()?.title}
                    </p>
                    {runningTimer.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 break-words">
                        {runningTimer.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Időmérő */}
              <div className="text-center py-3 sm:py-4">
                <div className={`text-3xl sm:text-4xl font-mono font-bold ${
                  runningTimer.isPaused 
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  {formatDuration(elapsedTime)}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 sm:mt-2">
                  {runningTimer.isPaused ? '⏸️ Szüneteltetve' : '▶️ Fut'}
                </p>
              </div>

              {/* Gombok */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                {runningTimer.isPaused ? (
                  <button
                    onClick={handleResume}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-sm"
                  >
                    <PlayIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>{t('timeTracking.resume') || 'Folytatás'}</span>
                  </button>
                ) : (
                  <button
                    onClick={handlePause}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-sm"
                  >
                    <PauseIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>{t('timeTracking.pause') || 'Szünet'}</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    handleStop();
                    setIsDetailsOpen(false);
                  }}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium text-sm"
                >
                  <StopIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Stop</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
